import React from "react";
import * as Sentry from "@sentry/react";
import { useEffect, useState } from "react";
import { Box, Card, CircularProgress, Typography, Tooltip, useTheme } from "@mui/material";
import { Query } from "system/query/Query";
import APIRequest, { APIError } from "system/APIRequest";
import SparklineContent from "components/widgets/charts/Sparkline/SparklineContent";
import WordCloudContent from "components/widgets/charts/WordCloud/WordCloudContent";
import { TextLoader } from "components/apiHandling/TextLoader";
import { calculatePercentChange, getPercentChangeTextColor } from "components/widgets/charts/PercentChange/PercentChange";
import ISONumber from "components/formatting/ISONumber";
import { useTranslation } from "react-i18next";

interface QueryDrilldownOverviewContentProps {
  drilldownQuery?: Query;
  activeQueryIds?: string[];
  data?: any;
  requestInfo?: any;
}

interface Response {
  analyzed_count: number;
  count_is_exact: boolean;
  est_total_negative_count: number;
  est_total_positive_count: number;
  est_total_words: number;
  max_timestamp: string;
  min_timestamp: string;
  negative_count: number;
  positive_count: number;
  positivity: number;
  timeline: number[];
  timeline_distribution: number[];
  timeline_timestamps: string[];
  topics: any[];
  total_count: number;
  value_labels: any;
  word_count: number;
}

export function QueryDrilldownOverviewContent(props: QueryDrilldownOverviewContentProps): JSX.Element {
  const theme = useTheme();
  const {i18n} = useTranslation();

  const [apiResponse, setApiResponse] = useState<Response>();
  const [apiIsLoading, setApiIsLoading] = useState<boolean>(false);
  const [apiError, setApiError] = useState<APIError | undefined>();

  const apiLoaded = !apiIsLoading && !!apiResponse && !apiError;

  // Fetch data
  useEffect(() => {
    if (!props.activeQueryIds) {
      return;
    }

    const topicsRequestBody = { "topics": { "size": 50 } };
    const localRequestBody = {...(props.drilldownQuery || {}), ...topicsRequestBody};

    setApiIsLoading(true);
    APIRequest(`query/topics/${props.activeQueryIds.join(",")}`, {}, {
      method: "POST",
      "body": JSON.stringify(localRequestBody)
    }).then((response) => {
      setApiIsLoading(false);
      setApiResponse(response || {});
    }).catch((err: APIError) => {
      console.error(`Error fetching data: ${err}`)
      Sentry.captureException(err);
      setApiIsLoading(false);
      setApiError(err);
    });
  }, [props.drilldownQuery, props.activeQueryIds]);

  const dataRecordsCount = () => {
    return (
      <Box sx={{ textAlign: "center"}}>
        <ISONumber
          typographyVariant="h3"
          sx={{ height:"100%", position:"relative", textAlign: "center" }}
          number={apiResponse?.total_count || 0} />
      </Box>
    )
  }

  const dataPercentChange = () => {
    let percentChange = calculatePercentChange(apiResponse?.timeline || []);
    percentChange = percentChange * 100;
    percentChange = Math.round(percentChange * 10) / 10;
    let percentColor = getPercentChangeTextColor(theme, percentChange);

    return (
      <Typography variant="h3" sx={{ textAlign: "center" }}>
        <Tooltip title="Percent Change" followCursor={true}>
          <span style={{ color: percentColor }}>{percentChange}%</span>
        </Tooltip>
      </Typography>
    )
  };

  const dataSentiment = () => {
    const countData = apiResponse?.topics.map((topic) => {
      return topic.count;
    });
    const positiveData = apiResponse?.topics.map((topic) => {
      return topic.positive_count;
    });
    const negativeData = apiResponse?.topics.map((topic) => {
      return topic.negative_count;
    });

    let countTotal = countData?.reduce((a, b) => a + b, 0);
    let positiveTotal = positiveData?.reduce((a, b) => a + b, 0);
    let negativeTotal = negativeData?.reduce((a, b) => a + b, 0);

    let positivePercent = positiveTotal / countTotal;
    let negativePercent = negativeTotal / countTotal;

    positivePercent = Math.round(positivePercent * 100);
    negativePercent = Math.round(negativePercent * 100);

    return (
      <Box sx={{ display: "flex", justifyContent: "space-between", height: "35px", margin: "10px 10px 0 10px", borderRadius: theme.general.borderRadius }}>
        <Tooltip title={`Positive: ${positivePercent}%`} followCursor={true}>
          <Box sx={{ width: `${positivePercent}%`, height: "100%", backgroundColor: theme.colors.namedColors.positive, borderRadius: "6px 0 0 6px" }}/>
        </Tooltip>
        <Tooltip title={`Neutral: ${100 - positivePercent - negativePercent}%`} followCursor={true}>
          <Box sx={{ width: `${100 - positivePercent - negativePercent}%`, height: "100%", backgroundColor: theme.colors.namedColors.neutral, margin: "0 3px 0 3px" }}/>
        </Tooltip>
        <Tooltip title={`Negative: ${negativePercent}%`} followCursor={true}>
          <Box sx={{ width: `${negativePercent}%`, height: "100%", backgroundColor: theme.colors.namedColors.negative, borderRadius: "0 6px 6px 0" }}/>
        </Tooltip>
      </Box>
    );
  }

  return (
    <>
      {/* Cards */}
      <Box
        sx={{
          my: 1,
          display: "flex",
          justifyContent: "center"
        }}>
        <Box
          sx={{
            mr: 1,
            width: "33%"
          }}>
          {/* Count */}
          <Card sx={{ height: "100px", borderRadius: `${theme.general.borderRadius}`, padding: `${theme.spacing(1)}` }}>
            <Typography variant="subtitle2" sx={{
              ml: 1
            }}>{i18n.t('records')}</Typography>
            {apiIsLoading && ( <Box
              sx={{
                mt: 1,
                textAlign: "center"
              }}><CircularProgress /></Box> )}
            {apiLoaded && ( dataRecordsCount() )}
          </Card>
        </Box>
        <Box
          sx={{
            mr: 1,
            width: "33%"
          }}>
          {/* Change */}
          <Card sx={{ height: "100px", borderRadius: `${theme.general.borderRadius}`, padding: `${theme.spacing(1)}` }}>
            <Typography variant="subtitle2" sx={{
              ml: 1
            }}>{i18n.t('change')}</Typography>
            {apiIsLoading && ( <Box
              sx={{
                mt: 1,
                textAlign: "center"
              }}><CircularProgress /></Box> )}
            {apiLoaded && ( dataPercentChange() )}
          </Card>
        </Box>
        <Box sx={{ width: "33%" }}>
          {/* Sentiment */}
          <Card sx={{ height: "100px", borderRadius: `${theme.general.borderRadius}`, padding: `${theme.spacing(1)}` }}>
            <Typography variant="subtitle2" sx={{
              ml: 1
            }}>{i18n.t('sentiment')}</Typography>
            {apiIsLoading && ( <Box
              sx={{
                mt: 1,
                textAlign: "center"
              }}><CircularProgress /></Box> )}
            {apiLoaded && ( dataSentiment() )}
          </Card>
        </Box>
      </Box>

      {/* Charts */}
      <Box sx={{ display: "flex" }}>
        {/* Sparkline */}
        <Card sx={{
          width: "50%",
          height: "350px",
          borderRadius: `${theme.general.borderRadius}`,
          overflow: "hidden",
          marginRight: `${theme.spacing(1)}`
        }}>
          {apiIsLoading && (
            <Box
              sx={{
                mt: 10,
                textAlign: "center"
              }}>
              <CircularProgress />
              <TextLoader />
            </Box>
          )}
          {apiLoaded && (
            <SparklineContent
              apiResponseBody={apiResponse}
              isDrilldown={true}
              defaultState={{ showPercentChange: false, showDots: true, showLabel: false,  }}
            />
          )}
        </Card>

        {/* Word Cloud */}
        <Card sx={{
          width: "50%",
          height: "350px",
          borderRadius: `${theme.general.borderRadius}`
        }}>
          {apiIsLoading && (
            <Box
              sx={{
                mt: 10,
                textAlign: "center"
              }}>
              <CircularProgress />
              <TextLoader />
            </Box>
          )}
          {apiLoaded && (
              <WordCloudContent
                apiResponses={apiResponse}
                isDrilldown={true}
              />
          )}
        </Card>
      </Box>
    </>
  );
}
